import * as echarts from 'echarts';
import * as ExcelJS from 'exceljs';

import DownloadUtils from '@/utils/DownloadUtils';
import i18n from '@/vueI18n';

interface IAvailsResultData {
  deliveryGoal: {
    delivered: number;
    likelyToDeliver: number;
    competing: number;
    unavailable: number;
  };
  forecast: {
    delivered: number;
    available: number;
    competing: number;
    booked: number;
  };
}

class AvailsResultExportHelper {
  private static getkeyLabel(key: keyof IAvailsResultData['deliveryGoal'] | keyof IAvailsResultData['forecast']) {
    return i18n.global.t(`availsResult.categories.labels.${key}`);
  }

  private static processData(data: IAvailsResultData): {
    deliveryGoal: [string, number][];
    forecast: [string, number][];
  } {
    return {
      deliveryGoal: Object.entries(data.deliveryGoal).map(([key, value]) => [
        AvailsResultExportHelper.getkeyLabel(key as keyof IAvailsResultData['deliveryGoal']),
        value,
      ]),
      forecast: Object.entries(data.forecast).map(([key, value]) => [
        AvailsResultExportHelper.getkeyLabel(key as keyof IAvailsResultData['forecast']),
        value,
      ]),
    };
  }

  public static async downloadAsXLSX(data: IAvailsResultData) {
    const workbook = new ExcelJS.Workbook();
    const deliveryGoalWorksheet = workbook.addWorksheet('Delivery Goal');
    const forecastWorksheet = workbook.addWorksheet('Forecast');
    const processedData = AvailsResultExportHelper.processData(data);
    deliveryGoalWorksheet.addRows(processedData.deliveryGoal);
    forecastWorksheet.addRows(processedData.forecast);
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new window.Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    DownloadUtils.saveBlobToFile(blob, 'avails-result-graph.xlsx');
  }

  public static async downloadAsCSV(data: IAvailsResultData) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Delivery Goal & Forecast');
    const processedData = AvailsResultExportHelper.processData(data);

    worksheet.addRows([...processedData.deliveryGoal, ...processedData.forecast]);
    const buffer = await workbook.csv.writeBuffer();
    const BOM = '\uFEFF';
    const blob = new window.Blob([BOM + buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    DownloadUtils.saveBlobToFile(blob, 'avails-result-graph.csv');
  }

  public static downloadAsImage(chartInstance: echarts.ECharts) {
    if (!chartInstance) throw new Error('Cannot reach DOM instance to download image');

    const chartWidth = chartInstance.getWidth();
    const dashboardWidth = document.getElementById('main-content').offsetWidth;
    const chartPercentageRatio = (chartWidth * 100) / dashboardWidth;
    const chartRatio = chartPercentageRatio / 90; // 90 represents 90%, which is the approximate size a chart that takes all the space in a card
    const pixelRatio = 1 - chartRatio <= 0 ? 1 : 1 + chartRatio;
    const base64Url = chartInstance.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' });
    DownloadUtils.downloadAsImg('avails-result-graph', base64Url);
  }
}

export default AvailsResultExportHelper;
